// # Expense Analytics Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { 
  FaChartLine, 
  FaChartPie, 
  FaChartBar,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaDollarSign,
  FaFilter,
  FaTimes,
  FaClock,
  FaTags,
  FaProjectDiagram
} from 'react-icons/fa';
import './expense_analytics.css';
import { fetchExpensesForDateRange, formatExpenseDataForCharts } from './expense_main_calc';

const ExpenseAnalytics = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateRange, setDateRange] = useState(tempDateRange);
  const [patternView, setPatternView] = useState('monthly');
  const [analyticsData, setAnalyticsData] = useState({
    expensesOverTime: [],
    expensesByCategory: [],
    currencyDistribution: [],
    monthlyExpenses: [],
    dailyExpenses: [],
    yearOverYear: [],
    yearlyExpenses: [],
    monthlyPatternForCurrentYear: [],
    stats: {
      totalExpenses: 0,
      currentMonthExpenses: 0,
      currentYearExpenses: 0,
      averageTransaction: 0,
      mostUsedCurrency: '',
      topCategory: '',
      categoryCount: 0,
      highestExpense: 0,
      monthlyAverage: 0,
      highestMonth: '',
      dailyAverage: 0,
      monthlyGrowth: 0,
      yearlyGrowth: 0,
      yearlyProjection: 0
    }
  });

  // # Color palette for charts
  const COLORS = [
    'var(--pastel-blue)',
    'var(--pastel-green)',
    'var(--pastel-yellow)',
    'var(--pastel-purple)',
    'var(--pastel-pink)',
    'var(--pastel-orange)'
  ];

  // # Fetch and process analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // # Fetch expenses for the selected date range
        const filteredExpenses = await fetchExpensesForDateRange(
          dateRange.startDate, 
          dateRange.endDate
        );
        
        // # Format data for charts and stats
        const formattedData = formatExpenseDataForCharts(filteredExpenses);
        
        // # Process additional data for charts
        const enhancedData = processAdditionalChartData(filteredExpenses, formattedData);
        
        setAnalyticsData(enhancedData);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [dateRange]);

  // # Process additional chart data not covered by the utility functions
  const processAdditionalChartData = (expenses, baseData) => {
    // # Category Trends Over Time
    const categoryTrends = {};
    expenses.forEach(expense => {
      const date = new Date(expense.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!categoryTrends[monthKey]) {
        categoryTrends[monthKey] = {};
      }
      categoryTrends[monthKey][expense.category] = (categoryTrends[monthKey][expense.category] || 0) + Number(expense.egp_total);
    });

    // # Monthly Comparison
    const monthlyComparison = Object.entries(baseData.expensesOverTime.reduce((acc, item) => {
      acc[item.month] = item.amount;
      return acc;
    }, {})).map(([month, amount]) => {
      const [year, monthNum] = month.split('-');
      const prevYear = `${Number(year) - 1}-${monthNum}`;
      const prevYearAmount = baseData.expensesOverTime.find(item => item.month === prevYear)?.amount || 0;
      return {
        month,
        currentYear: amount,
        previousYear: prevYearAmount,
        average: baseData.stats.monthlyAverage
      };
    });

    // # Enhanced Year over Year Comparison
    const yearOverYearData = baseData.yearlyExpenses.map(item => {
      const prevYearData = baseData.yearlyExpenses.find(y => Number(y.year) === Number(item.year) - 1);
      const prevYearAmount = prevYearData ? prevYearData.amount : 0;
      const growth = prevYearAmount ? ((item.amount - prevYearAmount) / prevYearAmount) * 100 : 0;
      return {
        year: item.year,
        amount: item.amount,
        previousYear: prevYearAmount,
        growth
      };
    });

    return {
      ...baseData,
      categoryTrendsData: Object.entries(categoryTrends).map(([month, categories]) => ({
        month,
        ...categories
      })),
      monthlyComparison,
      yearOverYearData
    };
  };

  // # Handle date range change in dialog
  const handleTempDateChange = (e) => {
    const { name, value } = e.target;
    setTempDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Apply date filter
  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    setIsFilterDialogOpen(false);
  };

  // # Cancel date filter
  const handleCancelFilter = () => {
    setTempDateRange(dateRange);
    setIsFilterDialogOpen(false);
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="expn_altc_page">
      <SharedHeader />
      
      <main className="expn_altc_content">
        <div className="expn_altc_title-container">
          <h1>Expense Analytics</h1>
          <button 
            className="expn_altc_filter-button"
            onClick={() => setIsFilterDialogOpen(true)}
          >
            <FaFilter /> Filter by Date
          </button>
        </div>

        {/* Filter Dialog */}
        {isFilterDialogOpen && (
          <div className="expn_altc_dialog-overlay">
            <div className="expn_altc_dialog">
              <div className="expn_altc_dialog-header">
                <h2 className="expn_altc_dialog-title">Filter by Date Range</h2>
                <button 
                  className="expn_altc_dialog-close"
                  onClick={handleCancelFilter}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="expn_altc_date-filter">
                <div className="expn_altc_date-inputs">
                  <div className="expn_altc_date-field">
                    <label>Start Date:</label>
                    <input
                      type="date"
                      name="startDate"
                      value={tempDateRange.startDate}
                      onChange={handleTempDateChange}
                      max={tempDateRange.endDate}
                    />
                  </div>
                  <div className="expn_altc_date-field">
                    <label>End Date:</label>
                    <input
                      type="date"
                      name="endDate"
                      value={tempDateRange.endDate}
                      onChange={handleTempDateChange}
                      min={tempDateRange.startDate}
                    />
                  </div>
                </div>
                <div className="expn_altc_dialog-actions">
                  <button 
                    className="expn_altc_dialog-button cancel"
                    onClick={handleCancelFilter}
                  >
                    Cancel
                  </button>
                  <button 
                    className="expn_altc_dialog-button apply"
                    onClick={handleApplyFilter}
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tabs */}
        <div className="expn_altc_tabs">
          <button 
            className={`expn_altc_tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaChartLine /> Overview Analysis
          </button>
          <button 
            className={`expn_altc_tab ${activeTab === 'category' ? 'active' : ''}`}
            onClick={() => setActiveTab('category')}
          >
            <FaChartPie /> Category Analysis
          </button>
          <button 
            className={`expn_altc_tab ${activeTab === 'trends' ? 'active' : ''}`}
            onClick={() => setActiveTab('trends')}
          >
            <FaChartBar /> Time Trends
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="expn_altc_loading">Loading analytics data...</div>
        ) : (
          <div className="expn_altc_dashboard">
            {/* Overview Analysis Tab */}
            {activeTab === 'overview' && (
              <div className="expn_altc_section">
                {/* Overview Stats Cards */}
                <div className="expn_altc_stats-grid">
                  <div className="expn_altc_stat-card">
                    <FaMoneyBillWave className="expn_altc_stat-icon" />
                    <h3>Total Expenses</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.totalExpenses.toLocaleString()}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaCalendarAlt className="expn_altc_stat-icon" />
                    <h3>Current Month</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.currentMonthExpenses.toLocaleString()}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaDollarSign className="expn_altc_stat-icon" />
                    <h3>Average Transaction</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.averageTransaction.toLocaleString()}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaMoneyBillWave className="expn_altc_stat-icon" />
                    <h3>Most Used Currency</h3>
                    <p className="expn_altc_stat-value">
                      {analyticsData.stats.mostUsedCurrency}
                    </p>
                  </div>
                </div>

                {/* Expenses Over Time Chart */}
                <div className="expn_altc_chart-container">
                  <h3>Expenses Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData.expensesOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#4a90e2" 
                        name="Expenses (EGP)" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Currency Distribution Chart */}
                <div className="expn_altc_chart-container">
                  <h3>Main Currency Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData.currencyDistribution}
                        dataKey="count"
                        nameKey="currency"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {analyticsData.currencyDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Category Analysis Tab */}
            {activeTab === 'category' && (
              <div className="expn_altc_section">
                {/* Category Stats Cards */}
                <div className="expn_altc_stats-grid">
                  <div className="expn_altc_stat-card">
                    <FaTags className="expn_altc_stat-icon" />
                    <h3>Top Spending Category</h3>
                    <p className="expn_altc_stat-value">
                      {analyticsData.stats.topCategory}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaProjectDiagram className="expn_altc_stat-icon" />
                    <h3>Category Count</h3>
                    <p className="expn_altc_stat-value">
                      {analyticsData.stats.categoryCount}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaMoneyBillWave className="expn_altc_stat-icon" />
                    <h3>Highest Category Expense</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.highestExpense.toLocaleString()}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaCalendarAlt className="expn_altc_stat-icon" />
                    <h3>Monthly Category Average</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.monthlyAverage.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Category Distribution Chart */}
                <div className="expn_altc_chart-container">
                  <h3>Category Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData.expensesByCategory}
                        dataKey="amount"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {analyticsData.expensesByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Category Trends Over Time */}
                <div className="expn_altc_chart-container">
                  <h3>Category Trends Over Time</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analyticsData.categoryTrendsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(analyticsData.categoryTrendsData[0] || {})
                        .filter(key => key !== 'month')
                        .map((category, index) => (
                          <Line
                            key={category}
                            type="monotone"
                            dataKey={category}
                            stroke={COLORS[index % COLORS.length]}
                            name={category}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Category-wise Spending */}
                <div className="expn_altc_chart-container">
                  <h3>Category-wise Spending</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.expensesByCategory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="amount" fill="#4a90e2" name="Amount (EGP)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Time Trends Tab */}
            {activeTab === 'trends' && (
              <div className="expn_altc_section">
                {/* Time Trends Stats Cards */}
                <div className="expn_altc_stats-grid">
                  <div className="expn_altc_stat-card">
                    <FaCalendarAlt className="expn_altc_stat-icon" />
                    <h3>Highest Spending Month</h3>
                    <p className="expn_altc_stat-value">
                      {analyticsData.stats.highestMonth}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaClock className="expn_altc_stat-icon" />
                    <h3>Daily Average</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.dailyAverage.toLocaleString()}
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaChartLine className="expn_altc_stat-icon" />
                    <h3>Monthly Growth Rate</h3>
                    <p className="expn_altc_stat-value">
                      {analyticsData.stats.monthlyGrowth.toFixed(2)}%
                    </p>
                  </div>
                  <div className="expn_altc_stat-card">
                    <FaProjectDiagram className="expn_altc_stat-icon" />
                    <h3>Yearly Projection</h3>
                    <p className="expn_altc_stat-value">
                      E£ {analyticsData.stats.yearlyProjection.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Monthly Comparison */}
                <div className="expn_altc_chart-container">
                  <h3>Monthly Comparison</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.monthlyComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="currentYear" fill={COLORS[0]} name="Current Year" />
                      <Bar dataKey="previousYear" fill={COLORS[1]} name="Previous Year" />
                      <Line
                        type="monotone"
                        dataKey="average"
                        stroke={COLORS[2]}
                        name="Monthly Average"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Daily Expense Pattern */}
                <div className="expn_altc_chart-container">
                  <h3>Expense Patterns</h3>
                  
                  {/* Pattern Type Tabs */}
                  <div className="expn_altc_pattern_tabs">
                    <button 
                      className={`expn_altc_pattern_tab ${patternView === 'monthly' ? 'active' : ''}`}
                      onClick={() => setPatternView('monthly')}
                    >
                      Monthly Pattern
                    </button>
                    <button 
                      className={`expn_altc_pattern_tab ${patternView === 'yearly' ? 'active' : ''}`}
                      onClick={() => setPatternView('yearly')}
                    >
                      Yearly Pattern
                    </button>
                  </div>
                  
                  {/* Monthly Pattern View */}
                  {patternView === 'monthly' && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.monthlyPatternForCurrentYear}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          dataKey="amount" 
                          fill={COLORS[0]} 
                          name="Monthly Expenses (EGP)" 
                        />
                        <Line
                          type="monotone"
                          dataKey="amount"
                          stroke={COLORS[1]}
                          name="Trend"
                          dot={false}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {/* Yearly Pattern View */}
                  {patternView === 'yearly' && (
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={analyticsData.yearlyExpenses}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          dataKey="amount" 
                          fill={COLORS[0]} 
                          name="Yearly Expenses (EGP)" 
                        />
                        <Line
                          type="monotone"
                          dataKey="amount"
                          stroke={COLORS[1]}
                          name="Trend"
                          dot={true}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Year-over-Year Comparison */}
                <div className="expn_altc_chart-container">
                  <h3>Year-over-Year Comparison</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={analyticsData.yearOverYearData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis yAxisId="amount" />
                      <YAxis yAxisId="growth" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar 
                        dataKey="amount" 
                        fill={COLORS[0]} 
                        name="Current Year" 
                        yAxisId="amount"
                      />
                      <Bar 
                        dataKey="previousYear" 
                        fill={COLORS[1]} 
                        name="Previous Year" 
                        yAxisId="amount"
                      />
                      <Line
                        type="monotone"
                        dataKey="growth"
                        stroke={COLORS[2]}
                        name="Growth %"
                        yAxisId="growth"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default ExpenseAnalytics;
